import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Stack,
  TextField,
  Grid,
  Paper,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  alpha,
  // Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Tooltip,
  useTheme,
  Autocomplete,
  Fade,
} from '@mui/material';
import {
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  // ArrowUpward as ArrowUpwardIcon,
  CheckCircle as CheckCircleIcon,
  // Pending as PendingIcon,
  ArrowForward as ArrowForwardIcon,
  Summarize as SummarizeIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { DataContext } from '../../context/DataContext';
import {
  generateTransactionBillHTML,
  getBillStyles
} from '../../components/billPrintUtils';
import '../../styles/ag-grid-premium-theme.css'

ModuleRegistry.registerModules([AllCommunityModule]);

// Custom Hooks
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Custom Cell Renderers
// const StatusRenderer = (props) => {
//   const isCancelled = (transaction) => {
//     return (
//       transaction?.transactionDetails?.type === "cancelled" ||
//       transaction?.status === "cancelled" ||
//       transaction?.isCancelled === true ||
//       transaction?.transactionDetails?.isCancelled === true ||
//       transaction?.cancelledAt !== undefined
//     );
//   };

//   const cancelled = isCancelled(props.data);

//   if (cancelled) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//         <Chip
//           label="CANCELLED"
//           size="small"
//           color="error"
//           variant="filled"
//           sx={{
//             fontWeight: 700,
//             borderRadius: '8px',
//             minWidth: '100px',
//             backdropFilter: 'blur(10px)',
//             backgroundColor: 'rgba(220, 38, 38, 0.15)',
//             border: '1px solid rgba(220, 38, 38, 0.3)',
//             textDecoration: 'line-through',
//           }}
//           icon={<CancelIcon sx={{ fontSize: '14px', color: '#DC2626' }} />}
//         />
//       </Box>
//     );
//   }

//   const pendingAmount = parseInt(parseFloat(props.data?.summary?.pendingAmount || props.data?.calculatedTotals?.fineBalance || 0).toFixed(2)) || 0;
//   const isPaid = pendingAmount === 0;

//   return (
//     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//       <Chip
//         label={isPaid ? 'Paid' : 'Pending'}
//         size="small"
//         color={isPaid ? 'success' : 'warning'}
//         variant="filled"
//         sx={{
//           fontWeight: 600,
//           borderRadius: '8px',
//           minWidth: '80px',
//           backdropFilter: 'blur(10px)',
//           backgroundColor: isPaid
//             ? 'rgba(16, 185, 129, 0.15)'
//             : 'rgba(245, 158, 11, 0.15)',
//           border: isPaid
//             ? '1px solid rgba(16, 185, 129, 0.3)'
//             : '1px solid rgba(245, 158, 11, 0.3)',
//         }}
//         icon={
//           isPaid ? (
//             <CheckCircleIcon sx={{ fontSize: '14px', color: '#10B981' }} />
//           ) : (
//             <PendingIcon sx={{ fontSize: '14px', color: '#F59E0B' }} />
//           )
//         }
//       />
//     </Box>
//   );
// };

const AmountRenderer = (props) => {
  const value = props.value || 0;

  return (
    <Box sx={{
      textAlign: 'right',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
    }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 0,
        }).format(value)}
      </Typography>
    </Box>
  );
};

const CustomerNameRenderer = (props) => {
  const { customers } = props.context;
  const customer = customers.find(c => c.id === props.value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 600,
        fontSize: '0.875rem',
        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
      }}>
        {customer?.name?.charAt(0) || 'C'}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {customer?.name || 'N/A'}
        </Typography>
        {customer?.contact && (
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            fontSize: '0.7rem',
          }}>
            {customer.contact}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ItemsRenderer = (props) => {
  // Get items count from the nested structure
  let itemsCount = 0;
  if (props.value && Array.isArray(props.value)) {
    itemsCount = props.value.reduce((total, item) => {
      if (item.items && Array.isArray(item.items)) {
        return total + item.items.length;
      }
      return total + 1;
    }, 0);
  }

  return (
    <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
      <Chip
        label={`${itemsCount} item${itemsCount !== 1 ? 's' : ''}`}
        size="small"
        variant="filled"
        sx={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(167, 139, 250, 0.15))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          color: '#8B5CF6',
          fontWeight: 600,
          borderRadius: '8px',
          minWidth: '70px',
        }}
        icon={<InventoryIcon sx={{ fontSize: '14px', color: '#8B5CF6' }} />}
      />
    </Box>
  );
};

const TransactionHistory = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { customerTransactions, customers, loading: contextLoading, refresh, removeCustomerTransactionAndUpdateStock, stock } = useContext(DataContext);

  // Refs
  const gridRef = useRef();

  // States
  const isPageLoading = false;
  const isSearching = false;
  const isFiltering = false;
  const [isExporting, setIsExporting] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null
  });
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Debounced search
  const searchTerm = useDebounce(searchInput, 300);

  // Helper function
  function getCustomerName(customerId) {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'N/A';
  }

  // Initialize default dates (current financial year)
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    let startDate, endDate;

    if (currentMonth >= 3) {
      startDate = new Date(currentYear, 3, 1);
      endDate = today;
    } else {
      startDate = new Date(currentYear - 1, 3, 1);
      endDate = today;
    }

    setDates({ startDate, endDate });
  }, []);

  // Apply all filters
  const applyFilters = useCallback(() => {
    if (!customerTransactions || customerTransactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...customerTransactions];

    // 1. Filter by customer
    if (customerFilter !== 'all') {
      filtered = filtered.filter(transaction =>
        transaction.customer === customerFilter
      );
    }

    // 2. Filter by date range
    if (dates.startDate && dates.endDate) {
      const startDateStr = format(dates.startDate, 'dd/MM/yyyy');
      const endDateStr = format(dates.endDate, 'dd/MM/yyyy');
      filtered = filterByDateRange(filtered, startDateStr, endDateStr);
    }

    // 3. Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction => {
        const customer = customers.find(c => c.id === transaction.customer);
        const customerNameMatch = customer?.name?.toLowerCase().includes(term);
        const billNoMatch = transaction.transactionDetails?.billNo?.toString().includes(term);
        const itemsMatch = transaction.items?.some(item =>
          item.name?.toLowerCase().includes(term)
        );
        const dateMatch = transaction.date?.toLowerCase().includes(term);

        return customerNameMatch || billNoMatch || itemsMatch || dateMatch;
      });
    }

    // 4. Sort by creation time (newest first)
    filtered.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      const parseDate = (dateStr) => {
        try {
          return parse(dateStr, 'dd/MM/yyyy', new Date());
        } catch {
          return new Date(0);
        }
      };
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB - dateA;
    });

    setFilteredTransactions(filtered);
  }, [customerTransactions, customerFilter, dates.startDate, dates.endDate, searchTerm, customers]);

  // Apply filters whenever any filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [customerTransactions, customerFilter, dates.startDate, dates.endDate, searchTerm, applyFilters]);

  // React version of the filterByDateRange function
  function filterByDateRange(records, startDate, endDate) {
    if (!records || records.length === 0) return [];
    if (!startDate || !endDate) return records;

    const parseFilterDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
        const date = new Date(year, month, day);
        if (isNaN(date.getTime()) ||
          date.getDate() !== day ||
          date.getMonth() !== month ||
          date.getFullYear() !== year) return null;
        return date;
      }
      const isoDate = new Date(dateStr);
      return isNaN(isoDate.getTime()) ? null : isoDate;
    };

    const parseRecordDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;
      if (typeof dateStr === 'string') {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime()) &&
              date.getDate() === day &&
              date.getMonth() === month &&
              date.getFullYear() === year) return date;
          }
        }
        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) return isoDate;
      }
      return null;
    };

    const start = parseFilterDate(startDate);
    const end = parseFilterDate(endDate);

    if (!start || !end) return records;

    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);

    return records.filter(record => {
      if (!record.date) return false;
      const recordDate = parseRecordDate(record.date);
      if (!recordDate) return false;
      return recordDate >= start && recordDate <= endOfDay;
    });
  }

  // Show Alert
  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(''), 3000);
  }, []);

  // Handle Print Bill
  const handlePrintBill = useCallback((transaction) => {
    if (!transaction) return;

    const isGst = transaction.transactionDetails?.gstApplicable;
    const billHtml = generateTransactionBillHTML(transaction, customers);
    const styles = getBillStyles();

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the bill');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Bill - ${isGst ? 'GST Invoice' : 'Cash Memo'}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            ${styles}
            @media print {
              body {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              table {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              tr {
                page-break-inside: avoid !important;
                page-break-after: auto !important;
                break-inside: avoid !important;
              }
              thead {
                display: table-header-group !important;
              }
              tfoot {
                display: table-footer-group !important;
              }
            }
          </style>
        </head>
        <body>
          ${billHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  setTimeout(() => window.close(), 100);
                };
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }, [customers]);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState(null);
  const [refundAmounts, setRefundAmounts] = useState({
    cash: 0,
    bank: 0,
    total: 0
  });
  const [cancellingItems, setCancellingItems] = useState([]);
  const [isCancelling, setIsCancelling] = useState(false);

  // Handle refund amount changes
  const handleRefundChange = (type, value) => {
    const numValue = parseFloat(value) || 0;
    setRefundAmounts(prev => {
      const updated = {
        ...prev,
        [type]: numValue
      };
      updated.total = updated.cash + updated.bank;
      return updated;
    });
  };

  // Reset refund amounts when modal closes
  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setSelectedCancelTransaction(null);
    setRefundAmounts({ cash: 0, bank: 0, total: 0 });
    setCancellingItems([]);
  };

  const handleCancelBill = useCallback((transaction) => {
    console.log('Cancel Bill:', transaction);

    const parseJewelryName = (name) => {
      if (!name || typeof name !== 'string') {
        return {
          type: '',
          serialNo: '',
          metal: '',
          purity: '',
          originalName: name || '',
          isValid: false
        };
      }

      const matchingItems = stock.filter(stockItem =>
        stockItem.qr === name &&
        (stockItem.sold === "Yes" || stockItem.sold === true)
      );

      return {
        type: matchingItems[0]?.name || '',
        serialNo: matchingItems[0]?.serialNo || '',
        metal: matchingItems[0]?.metal || '',
        purity: matchingItems[0]?.melting || '',
        originalName: matchingItems[0]?.name || '',
        isValid: !!matchingItems[0]?.name || !!matchingItems[0]?.serialNo
      };
    };

    const findFirstMissingNumber = (arr, startFrom = 1) => {
      if (arr.length === 0) return startFrom;
      const numberSet = new Set(arr);
      let i = startFrom;
      while (numberSet.has(i)) {
        i++;
      }
      return i;
    };

    const getNextSerialNumber = (itemName, metal, currentStock, assignedInThisBatch = []) => {
      if (!itemName || !metal) return 1;

      const allItemsForType = currentStock.filter(item =>
        item.name === itemName && item.metal === metal
      );

      const allExistingSerialNumbers = allItemsForType
        .map(item => parseInt(item.serialNo) || 0)
        .filter(num => !isNaN(num) && num > 0)
        .sort((a, b) => a - b);

      const unsoldSerialNumbers = allItemsForType
        .filter(item => item.sold === "No" || item.sold === false)
        .map(item => parseInt(item.serialNo) || 0)
        .filter(num => !isNaN(num) && num > 0);

      const currentlyUsedNumbers = [...unsoldSerialNumbers, ...assignedInThisBatch];
      const maxExistingNumber = allExistingSerialNumbers.length > 0
        ? Math.max(...allExistingSerialNumbers)
        : 0;

      const missingNumber = findFirstMissingNumber(currentlyUsedNumbers, 1);

      if (missingNumber <= maxExistingNumber) {
        return missingNumber;
      }

      return maxExistingNumber + 1;
    };

    const itemsToCancel = [];

    const assignedSerialNumbersMap = new Map();

    for (let i = 0; i < transaction?.items?.length; i++) {
      const item = transaction.items[i];

      if (item?.items && Array.isArray(item.items)) {
        // Handle items with nested items array
        for (const subItem of item.items) {
          const parsedInfo = parseJewelryName(subItem?.qr);

          if (parsedInfo.isValid) {
            const matchingItems = stock.filter(stockItem =>
              stockItem.qr === subItem.qr &&
              (stockItem.sold === "Yes" || stockItem.sold === true)
            );

            if (matchingItems.length > 0) {
              const itemToUpdate = matchingItems[0];
              const itemKey = `${itemToUpdate.name}-${itemToUpdate.metal}`;

              if (!assignedSerialNumbersMap.has(itemKey)) {
                assignedSerialNumbersMap.set(itemKey, []);
              }
              const assignedNumbers = assignedSerialNumbersMap.get(itemKey);

              const conflictingItems = stock.filter(stockItem =>
                stockItem.qr === subItem.qr
              );

              let newSerialNumber;

              if (conflictingItems.length > 0) {
                newSerialNumber = getNextSerialNumber(
                  itemToUpdate.name,
                  itemToUpdate.metal,
                  stock,
                  assignedNumbers
                );
                assignedNumbers.push(newSerialNumber);
              } else {
                newSerialNumber = parsedInfo.serialNo;
              }

              itemsToCancel.push({
                ...itemToUpdate,
                updateData: {
                  sold: "No",
                  soldDescription: "",
                  serialNo: newSerialNumber
                },
                originalSerialNo: parsedInfo.serialNo,
                hasConflict: conflictingItems.length > 0,
                transactionItem: subItem
              });
            }
          }
        }
      }
    }

    if (itemsToCancel.length > 0) {
      setSelectedCancelTransaction(transaction);
      setCancellingItems(itemsToCancel);

      const paidAmount = transaction.summary?.paymentDone || transaction.calculatedTotals?.subtotal || 0;
      setRefundAmounts({
        cash: paidAmount,
        bank: 0,
        total: paidAmount
      });

      setCancelModalOpen(true);
    } else {
      alert(`No sold items found in bill ${transaction.transactionDetails?.billNo || 'N/A'} to cancel.`);
    }
  }, [stock]);

  const setCancelModal = useCallback((open, transaction) => {
    setCancelModalOpen(open);
    handleCancelBill(transaction);
  }, [handleCancelBill]);

  const generateTransactionExcelData = useCallback((transaction) => {
    // Helper function to parse jewelry name
    const parseJewelryName = (name) => {
      if (!name || typeof name !== 'string') {
        return { type: '', serialNo: '', isValid: false };
      }
      // Find matching stock item
      const matchingItem = stock.find(stockItem =>
        stockItem.qr === name &&
        (stockItem.sold === "Yes" || stockItem.sold === true)
      );

      return {
        type: matchingItem?.name || '',
        serialNo: matchingItem?.serialNo || '',
        isValid: !!matchingItem
      };
    };

    // Prepare data for Excel
    const excelData = [];

    // Add header row
    excelData.push([
      'S.No.',
      'Serial No.',
      'Item Name',
      'Gross Wt (g)',
      'CZ (g)',
      'Pearl (g)',
      'Belt (g)',
      'Other (g)',
      'Net Wt (g)',
      'Stone Cost (₹)',
      'HUID (₹)',
      'Total %',
      'Metal Rate (₹/g)',
      'Fine Wt (g)',
      'Fine Amount (₹)',
      'Total Amount (₹)'
    ]);

    // Collect all items from transaction
    const allItems = [];

    if (transaction.items && Array.isArray(transaction.items)) {
      transaction.items.forEach(categoryItem => {
        // Check if categoryItem has nested items (consolidated mode)
        if (categoryItem.items && Array.isArray(categoryItem.items)) {
          const itemsCount = categoryItem.items.length;

          // Calculate per-item HUID charge (total HUID divided by number of items)
          const totalHuidCharge = parseFloat(categoryItem.huidCharges) || 0;
          const perItemHuidCharge = itemsCount > 0 ? totalHuidCharge / itemsCount : 0;

          // Calculate per-item stone charge (total stone divided by number of items)
          const totalStoneCharge = parseFloat(categoryItem.stoneCharges) || 0;
          const perItemStoneCharge = itemsCount > 0 ? totalStoneCharge / itemsCount : 0;

          categoryItem.items.forEach(item => {
            // Get values from the item and category level
            const weight = parseFloat(item.weight) || 0;

            // Use item's totalPercentage if available, otherwise calculate from category
            let totalPercentage = 0;
            if (item.totalPercentage) {
              totalPercentage = parseFloat(item.totalPercentage);
            } else {
              const purity = parseFloat(categoryItem.purity) || 75;
              const wastage = parseFloat(categoryItem.wastage) || 0;
              totalPercentage = purity + wastage;
            }

            const totalFine = weight * (totalPercentage / 100);
            const metalRateNum = parseFloat(categoryItem.metalRate) || 0;

            let amountFromMetal = 0;
            if (metalRateNum > 0 && totalFine > 0) {
              amountFromMetal = metalRateNum * totalFine;
            }

            // Use individual item stone price if available, otherwise use per-item distributed value
            let stonePrice = parseFloat(item.stonePrice);
            if (isNaN(stonePrice) || stonePrice === 0) {
              stonePrice = perItemStoneCharge;
            }

            // Use individual item HUID cost if available, otherwise use per-item distributed value
            let huidCharge = parseFloat(item.huidCost);
            if (isNaN(huidCharge) || huidCharge === 0) {
              huidCharge = perItemHuidCharge;
            }

            const totalAmount = amountFromMetal + stonePrice + huidCharge;

            // Get serial number from QR or item
            let serialNo = item.serialNo || '-';
            if (item.qr) {
              const parsedInfo = parseJewelryName(item.qr);
              if (parsedInfo.isValid) {
                serialNo = parsedInfo.serialNo;
              }
            }

            allItems.push({
              serialNo: serialNo,
              name: item.name || categoryItem.name,
              grossWeight: item.grossWeight || weight,
              czWeight: item.czWeight || 0,
              pearlWeight: item.pearlWeight || 0,
              beltWeight: item.beltWeight || 0,
              otherWeight: item.otherWeight || 0,
              netWeight: weight,
              stonePrice: stonePrice,
              huidCharge: huidCharge,
              totalPercentage: totalPercentage,
              metalRate: metalRateNum,
              totalFine: totalFine,
              amountFromMetal: amountFromMetal,
              totalAmount: totalAmount
            });
          });
        } else {
          // Handle non-nested items (separate mode)
          const weight = parseFloat(categoryItem.weight) || 0;

          // Calculate total percentage
          let totalPercentage = 0;
          if (categoryItem.totalPercentage) {
            totalPercentage = parseFloat(categoryItem.totalPercentage);
          } else {
            const purity = parseFloat(categoryItem.purity) || parseFloat(categoryItem.melting) || 75;
            const wastage = parseFloat(categoryItem.wastage) || 0;
            totalPercentage = purity + wastage;
          }

          const totalFine = weight * (totalPercentage / 100);
          const metalRateNum = parseFloat(categoryItem.metalRate) || 0;

          let amountFromMetal = 0;
          if (metalRateNum > 0 && totalFine > 0) {
            amountFromMetal = metalRateNum * totalFine;
          }

          // Stone and HUID charges for individual items
          const stonePrice = parseFloat(categoryItem.stonePrice) || parseFloat(categoryItem.stoneCharges) || 0;
          const huidCharge = parseFloat(categoryItem.huidCost) || parseFloat(categoryItem.huidCharges) || 0;
          const totalAmount = amountFromMetal + stonePrice + huidCharge;

          allItems.push({
            serialNo: categoryItem.serialNo || '-',
            name: categoryItem.name,
            grossWeight: categoryItem.grossWeight || weight,
            czWeight: categoryItem.czWeight || 0,
            pearlWeight: categoryItem.pearlWeight || 0,
            beltWeight: categoryItem.beltWeight || 0,
            otherWeight: categoryItem.otherWeight || 0,
            netWeight: weight,
            stonePrice: stonePrice,
            huidCharge: huidCharge,
            totalPercentage: totalPercentage,
            metalRate: metalRateNum,
            totalFine: totalFine,
            amountFromMetal: amountFromMetal,
            totalAmount: totalAmount
          });
        }
      });
    }

    // Add item rows
    allItems.forEach((item, idx) => {
      excelData.push([
        idx + 1,
        item.serialNo,
        item.name,
        item.grossWeight.toFixed(3),
        item.czWeight.toFixed(3),
        item.pearlWeight.toFixed(3),
        item.beltWeight.toFixed(3),
        item.otherWeight.toFixed(3),
        item.netWeight.toFixed(3),
        item.stonePrice.toFixed(2),
        item.huidCharge.toFixed(2),
        item.totalPercentage.toFixed(1) + '%',
        item.metalRate.toFixed(2),
        item.totalFine.toFixed(3),
        item.amountFromMetal.toFixed(2),
        item.totalAmount.toFixed(2)
      ]);
    });

    // Calculate totals
    const totals = {
      totalGrossWeight: allItems.reduce((sum, i) => sum + i.grossWeight, 0),
      totalCZWeight: allItems.reduce((sum, i) => sum + i.czWeight, 0),
      totalPearlWeight: allItems.reduce((sum, i) => sum + i.pearlWeight, 0),
      totalBeltWeight: allItems.reduce((sum, i) => sum + i.beltWeight, 0),
      totalOtherWeight: allItems.reduce((sum, i) => sum + i.otherWeight, 0),
      totalNetWeight: allItems.reduce((sum, i) => sum + i.netWeight, 0),
      totalStoneCost: allItems.reduce((sum, i) => sum + i.stonePrice, 0),
      totalHuidCharge: allItems.reduce((sum, i) => sum + i.huidCharge, 0),
      totalFine: allItems.reduce((sum, i) => sum + i.totalFine, 0),
      totalAmountFromMetal: allItems.reduce((sum, i) => sum + i.amountFromMetal, 0),
      totalAmount: allItems.reduce((sum, i) => sum + i.totalAmount, 0)
    };

    // Add totals row
    excelData.push([
      'TOTALS',
      '',
      '',
      totals.totalGrossWeight.toFixed(3),
      totals.totalCZWeight.toFixed(3),
      totals.totalPearlWeight.toFixed(3),
      totals.totalBeltWeight.toFixed(3),
      totals.totalOtherWeight.toFixed(3),
      totals.totalNetWeight.toFixed(3),
      totals.totalStoneCost.toFixed(2),
      totals.totalHuidCharge.toFixed(2),
      '',
      '',
      totals.totalFine.toFixed(3),
      totals.totalAmountFromMetal.toFixed(2),
      totals.totalAmount.toFixed(2)
    ]);

    // Add empty row
    excelData.push([]);

    // Add summary information
    excelData.push(['TRANSACTION SUMMARY']);
    excelData.push(['Bill No:', transaction.transactionDetails?.billNo || '-']);
    excelData.push(['Date:', transaction.date]);

    // Get payment details
    // const paidAmount = transaction.payment?.totalPaymentDone || 0;
    // const pendingAmount = transaction.summary?.pendingAmount || transaction.calculatedTotals?.fineBalance || 0;

    // Calculate grand total with GST
    let subtotal = totals.totalAmount;
    let grandTotal = subtotal;
    let cgst = 0, sgst = 0, igst = 0;
    const showGST = transaction.transactionDetails?.gstApplicable === true;
    const gstType = transaction.transactionDetails?.gstType || 'cgst_sgst';
    const gstMode = transaction.transactionDetails?.gstMode || 'add';

    if (showGST && subtotal > 0) {
      if (gstMode === 'add') {
        if (gstType === 'igst') {
          igst = subtotal * 0.03;
          grandTotal = subtotal + igst;
        } else {
          cgst = subtotal * 0.015;
          sgst = subtotal * 0.015;
          grandTotal = subtotal + cgst + sgst;
        }
      } else {
        // For adjust mode, the prices already include GST, so we don't add again
        grandTotal = subtotal;
      }
    }

    excelData.push(['Subtotal:', `₹${subtotal.toFixed(2)}`]);

    // Add GST if applicable and in add mode
    if (showGST && gstMode === 'add') {
      if (gstType === 'igst') {
        excelData.push(['IGST (3%):', `₹${igst.toFixed(2)}`]);
      } else {
        excelData.push(['CGST (1.5%):', `₹${cgst.toFixed(2)}`]);
        excelData.push(['SGST (1.5%):', `₹${sgst.toFixed(2)}`]);
      }
    }

    excelData.push(['Grand Total:', `₹${grandTotal.toFixed(2)}`]);

    // Payment breakdown
    if (transaction.payment?.cash > 0) {
      excelData.push(['Cash Payment:', `₹${transaction.payment.cash.toFixed(2)}`]);
    }
    if (transaction.payment?.online > 0) {
      excelData.push(['Online/Bank Payment:', `₹${transaction.payment.online.toFixed(2)}`]);
    }
    if (transaction.payment?.cheque > 0) {
      excelData.push(['Cheque Payment:', `₹${transaction.payment.cheque.toFixed(2)}`]);
    }
    if (transaction.payment?.other > 0) {
      excelData.push(['Other Payment:', `₹${transaction.payment.other.toFixed(2)}`]);
    }

    // Advance payment breakdown
    if (transaction.payment?.advancePayment?.enabled && transaction.payment.advancePayment.breakup) {
      excelData.push([]);
      excelData.push(['ADVANCE PAYMENT BREAKDOWN']);
      Object.entries(transaction.payment.advancePayment.breakup).forEach(([key, value]) => {
        if (parseFloat(value) > 0) {
          const label = key.charAt(0).toUpperCase() + key.slice(1);
          excelData.push([`${label}:`, `₹${parseFloat(value).toFixed(2)}`]);
        }
      });
      excelData.push(['Total Advance:', `₹${transaction.payment.advancePayment.total.toFixed(2)}`]);
    }

    // Fine payment if applicable
    if (transaction.payment?.finePayment?.enabled) {
      excelData.push([]);
      excelData.push(['FINE PAYMENT DETAILS']);
      excelData.push(['Fine Weight (gm):', transaction.payment.finePayment.weight?.toFixed(3) || '0']);
      excelData.push(['Purity (%):', transaction.payment.finePayment.purity?.toFixed(1) || '0']);
      excelData.push(['Actual Fine Received (gm):', transaction.payment.finePayment.actualFine?.toFixed(3) || '0']);
    }

    // Fine summary
    if (transaction.calculatedTotals?.totalFineWeight > 0) {
      excelData.push([]);
      excelData.push(['FINE SUMMARY']);
      excelData.push(['Total Fine Weight (gm):', transaction.calculatedTotals.totalFineWeight?.toFixed(3) || '0']);
      excelData.push(['Fine Payment Received (gm):', transaction.calculatedTotals.finePaymentReceived?.toFixed(3) || '0']);
      excelData.push(['Fine Balance (gm):', transaction.calculatedTotals.fineBalance?.toFixed(3) || '0']);
    }

    // Add GST status
    excelData.push([]);
    excelData.push(['GST STATUS:', showGST ? 'Applied' : 'Not Applied']);
    if (showGST) {
      excelData.push(['GST Type:', gstType === 'igst' ? 'IGST (3%)' : 'CGST + SGST (1.5% each)']);
      excelData.push(['GST Mode:', gstMode === 'add' ? 'Add GST' : 'Adjusted in Making Charges']);
    }

    return excelData;
  }, [stock]);

  // Add this function to handle downloading Excel for a single transaction
  const handleDownloadTransactionExcel = useCallback(async (transaction) => {
    if (!transaction) {
      showAlert("No transaction data to export", "error");
      return;
    }

    try {
      // Dynamically import xlsx
      const XLSX = await import('xlsx');

      // Generate Excel data for this transaction
      const excelData = generateTransactionExcelData(transaction);

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      ws['!cols'] = [
        { wch: 8 },   // S.No.
        { wch: 12 },  // Serial No.
        { wch: 25 },  // Item Name
        { wch: 12 },  // Gross Wt
        { wch: 10 },  // CZ
        { wch: 10 },  // Pearl
        { wch: 10 },  // Belt
        { wch: 10 },  // Other
        { wch: 12 },  // Net Wt
        { wch: 12 },  // Stone Cost
        { wch: 10 },  // HUID
        { wch: 10 },  // Total %
        { wch: 14 },  // Metal Rate
        { wch: 12 },  // Fine Wt
        { wch: 14 },  // Fine Amount
        { wch: 14 }   // Total Amount
      ];

      // Style the header row (first row)
      const headerRange = XLSX.utils.decode_range(ws['!ref']);
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true, sz: 11 },
            fill: { fgColor: { rgb: "4472C4" } },
          };
        }
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      const sheetName = `Bill_${transaction.transactionDetails?.billNo || transaction.id?.slice(-6)}`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename
      const fileName = `Bill_${transaction.transactionDetails?.billNo || 'Details'}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);

      showAlert("Excel file downloaded successfully!", "success");
    } catch (error) {
      console.error("Error generating Excel file:", error);
      showAlert("Failed to generate Excel file. Please try again.", "error");
    }
  }, [generateTransactionExcelData, showAlert]);

  // Helper function to get all items from transaction for the details modal - FIXED VERSION
  const getAllItemsForModal = useCallback((transaction) => {
    if (!transaction) return [];

    const allItems = [];

    // Check if transaction has items array
    if (transaction.items && Array.isArray(transaction.items)) {
      transaction.items.forEach(categoryItem => {
        // Each category item has its own items array (nested structure)
        if (categoryItem.items && Array.isArray(categoryItem.items)) {
          const itemsCount = categoryItem.items.length;

          // Calculate per-item HUID charge (total HUID divided by number of items)
          const totalHuidCharge = parseFloat(categoryItem.huidCharges) || 0;
          const perItemHuidCharge = itemsCount > 0 ? totalHuidCharge / itemsCount : 0;

          // Calculate per-item stone charge (total stone divided by number of items)
          const totalStoneCharge = parseFloat(categoryItem.stoneCharges) || 0;
          const perItemStoneCharge = itemsCount > 0 ? totalStoneCharge / itemsCount : 0;

          // Process each sub-item in the category
          categoryItem.items.forEach(item => {
            // Extract values from the item object based on your data structure
            const weight = parseFloat(item.weight) || 0;
            const totalPercentage = parseFloat(item.totalPercentage) || 0;
            const totalFine = weight * (totalPercentage / 100);
            const metalRate = parseFloat(categoryItem.metalRate || item.metalRate || 0);

            let amountFromMetal = 0;
            if (metalRate > 0 && totalFine > 0) {
              amountFromMetal = metalRate * totalFine;
            }

            // Use individual item stone price if available, otherwise use per-item distributed value
            let stonePrice = parseFloat(item.stonePrice);
            if (isNaN(stonePrice) || stonePrice === 0) {
              stonePrice = perItemStoneCharge;
            }

            // Use individual item HUID cost if available, otherwise use per-item distributed value
            let huidCharge = parseFloat(item.huidCost);
            if (isNaN(huidCharge) || huidCharge === 0) {
              huidCharge = perItemHuidCharge;
            }

            const totalAmount = amountFromMetal + stonePrice + huidCharge;

            allItems.push({
              name: categoryItem.name || item.name || 'N/A',
              serialNo: item.serialNo || '-',
              grossWeight: item.grossWeight || weight,
              czWeight: item.czWeight || 0,
              pearlWeight: item.pearlWeight || 0,
              beltWeight: item.beltWeight || 0,
              otherWeight: item.otherWeight || 0,
              netWeight: weight,
              stonePrice: stonePrice,
              huidCharge: huidCharge,
              totalPercentage: totalPercentage,
              metalRate: metalRate,
              totalFine: totalFine,
              amountFromMetal: amountFromMetal,
              totalAmount: totalAmount
            });
          });
        } else {
          // Handle non-nested items (separate mode)
          const weight = parseFloat(categoryItem.weight) || 0;

          // Calculate total percentage for separate items
          let totalPercentage = 0;
          if (categoryItem.totalPercentage) {
            totalPercentage = parseFloat(categoryItem.totalPercentage);
          } else {
            const purity = parseFloat(categoryItem.purity || categoryItem.melting || 75);
            const wastage = parseFloat(categoryItem.wastage || 0);
            totalPercentage = purity + wastage;
          }

          const totalFine = weight * (totalPercentage / 100);
          const metalRate = parseFloat(categoryItem.metalRate || 0);

          let amountFromMetal = 0;
          if (metalRate > 0 && totalFine > 0) {
            amountFromMetal = metalRate * totalFine;
          }

          // Stone and HUID charges for individual items (these are already per-item in separate mode)
          const stonePrice = parseFloat(categoryItem.stonePrice) || parseFloat(categoryItem.stoneCharges) || 0;
          const huidCharge = parseFloat(categoryItem.huidCost) || parseFloat(categoryItem.huidCharges) || 0;
          const totalAmount = amountFromMetal + stonePrice + huidCharge;

          allItems.push({
            name: categoryItem.name || 'N/A',
            serialNo: categoryItem.serialNo || '-',
            grossWeight: categoryItem.grossWeight || weight,
            czWeight: categoryItem.czWeight || 0,
            pearlWeight: categoryItem.pearlWeight || 0,
            beltWeight: categoryItem.beltWeight || 0,
            otherWeight: categoryItem.otherWeight || 0,
            netWeight: weight,
            stonePrice: stonePrice,
            huidCharge: huidCharge,
            totalPercentage: totalPercentage,
            metalRate: metalRate,
            totalFine: totalFine,
            amountFromMetal: amountFromMetal,
            totalAmount: totalAmount
          });
        }
      });
    }

    console.log('Extracted items for modal:', allItems);
    return allItems;
  }, []);

  // Calculate totals for the modal
  const calculateModalTotals = useCallback((allItems) => {
    return {
      totalGrossWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.grossWeight) || 0), 0),
      totalCZWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.czWeight) || 0), 0),
      totalPearlWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.pearlWeight) || 0), 0),
      totalBeltWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.beltWeight) || 0), 0),
      totalOtherWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.otherWeight) || 0), 0),
      totalNetWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.netWeight) || 0), 0),
      totalItems: allItems.length,
      totalStoneCost: allItems.reduce((sum, item) => sum + (parseFloat(item.stonePrice) || 0), 0),
      totalHuidCharge: allItems.reduce((sum, item) => sum + (parseFloat(item.huidCharge) || 0), 0),
      totalFine: allItems.reduce((sum, item) => sum + (parseFloat(item.totalFine) || 0), 0),
      totalAmountFromMetal: allItems.reduce((sum, item) => sum + (parseFloat(item.amountFromMetal) || 0), 0),
      totalAmount: allItems.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0)
    };
  }, []);

  const handleConfirmCancellation = async () => {
    setIsCancelling(true);
    try {
      const cancellationData = {
        transactionId: selectedCancelTransaction.id,
        billNo: selectedCancelTransaction.transactionDetails?.billNo,
        customerId: selectedCancelTransaction.customer,
        refund: {
          cash: refundAmounts.cash,
          bank: refundAmounts.bank,
          total: refundAmounts.total
        },
        items: cancellingItems.map(item => ({
          id: item.id,
          name: item.name,
          huid: item.huid,
          originalSerialNo: item.originalSerialNo,
          newSerialNo: item.updateData.serialNo,
          hasConflict: item.hasConflict,
          updateData: item.updateData
        })),
        silverItems: [],
        cancelledAt: new Date().toISOString()
      };

      let resp = await removeCustomerTransactionAndUpdateStock(cancellationData);
      console.log('Cancellation Response:', resp);

      showAlert(`Bill ${selectedCancelTransaction.transactionDetails?.billNo} has been cancelled successfully!`, 'success');
      refresh();
      handleCancelModalClose();

    } catch (error) {
      console.error('Error cancelling bill:', error);
      showAlert('Failed to cancel bill. Please try again.', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  const ActionsRenderer = useCallback((props) => {
    const { handleViewTransaction, handlePrintBill, handleDownloadExcel } = props.context;

    const isCancelled = (transaction) => {
      return (
        transaction?.transactionDetails?.type === "cancelled" ||
        transaction?.status === "cancelled" ||
        transaction?.isCancelled === true ||
        transaction?.transactionDetails?.isCancelled === true ||
        transaction?.cancelledAt !== undefined
      );
    };

    const cancelled = isCancelled(props.data);

    if (cancelled) {
      return (
        <Box sx={{
          display: 'flex',
          gap: 0.5,
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewTransaction(props.data)}
              sx={{
                color: '#6B7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                padding: '6px',
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Cancelled Bill">
            <IconButton
              size="small"
              onClick={() => handlePrintBill(props.data)}
              sx={{
                color: '#6B7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                padding: '6px',
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Excel">
            <IconButton
              size="small"
              onClick={() => handleDownloadExcel(props.data)}
              sx={{
                color: '#6B7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                padding: '6px',
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Chip
            label="Cancelled"
            size="small"
            color="error"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: '24px',
              textDecoration: 'line-through',
            }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewTransaction(props.data)}
            sx={{
              color: '#2563EB',
              backgroundColor: 'rgba(37, 99, 235, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel Bill">
          <IconButton
            size="small"
            onClick={() => setCancelModal(true, props.data)}
            sx={{
              color: '#b91010',
              backgroundColor: 'rgba(185, 16, 16, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download Excel">
          <IconButton
            size="small"
            onClick={() => handleDownloadExcel(props.data)}
            sx={{
              color: '#10B981',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, [setCancelModal]);

  // AG Grid Column Definitions
  const columnDefs = useMemo(() => [
    {
      headerName: '#',
      valueGetter: 'node.rowIndex + 1',
      width: 50,
      sortable: false,
      filter: false,
      pinned: 'left',
      cellStyle: {
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        color: '#6B7280',
      },
    },
    {
      field: 'date',
      headerName: 'DATE',
      width: 110,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        try {
          const date = parse(params.value, 'dd/MM/yyyy', new Date());
          return format(date, 'dd MMM');
        } catch {
          return params.value;
        }
      },
      cellStyle: {
        fontWeight: 600,
        color: '#374151',
        fontFamily: "'SF Mono', monospace",
      },
    },
    {
      field: 'customer',
      headerName: 'CUSTOMER',
      width: 250,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: CustomerNameRenderer,
      cellRendererParams: { context: { customers } },
    },
    {
      headerName: 'BILL NO',
      width: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        return params.data?.transactionDetails?.billNo || params.data?.billNo || params.data?.id?.slice(-6) || 'N/A';
      },
      cellStyle: {
        fontWeight: 700,
        color: '#1F2937',
        fontFamily: "'SF Mono', monospace",
      },
    },
    {
      field: 'items',
      headerName: 'ITEMS',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: ItemsRenderer,
    },
    {
      headerName: 'AMOUNT',
      width: 140,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (params) => {
        return params.data?.calculatedTotals?.subtotal || params.data?.summary?.grandTotal || 0;
      },
      cellRenderer: AmountRenderer,
      comparator: (valueA, valueB) => valueA - valueB,
    },
    // {
    //   headerName: 'STATUS',
    //   width: 120,
    //   filter: false,
    //   sortable: true,
    //   cellRenderer: StatusRenderer,
    //   valueGetter: (params) => {
    //     return params.data?.summary?.pendingAmount || params.data?.calculatedTotals?.fineBalance || 0;
    //   },
    // },
    {
      headerName: 'ACTIONS',
      width: 180,
      filter: false,
      sortable: false,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        context: {
          handleViewTransaction: (data) => {
            setSelectedTransaction(data);
            setOpenModal(true);
          },
          handlePrintBill: handlePrintBill,
          handleDownloadExcel: handleDownloadTransactionExcel,
        }
      },
      pinned: 'right',
    },
  ], [customers, handlePrintBill, handleDownloadTransactionExcel, ActionsRenderer]);

  // Default Column Definition
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
    wrapText: true,
    autoHeight: true,
    cellStyle: (params) => {
      const isCancelled = (transaction) => {
        return (
          transaction?.transactionDetails?.type === "cancelled" ||
          transaction?.status === "cancelled" ||
          transaction?.isCancelled === true ||
          transaction?.transactionDetails?.isCancelled === true ||
          transaction?.cancelledAt !== undefined
        );
      };

      const cancelled = isCancelled(params.data);

      return {
        color: cancelled ? (isDark ? '#9CA3AF' : '#6B7280') : theme.palette.text.primary,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        textDecoration: cancelled ? 'line-through' : 'none',
        opacity: cancelled ? 0.8 : 1,
      };
    },
  }), [isDark, theme.palette.text.primary]);

  const isCancelled = (transaction) => {
    return (
      transaction?.transactionDetails?.type === "cancelled" ||
      transaction?.status === "cancelled" ||
      transaction?.isCancelled === true ||
      transaction?.transactionDetails?.isCancelled === true ||
      transaction?.cancelledAt !== undefined
    );
  };

  // Grid Options
  const gridOptions = useMemo(() => ({
    rowBuffer: 20,
    cacheBlockSize: 100,
    maxBlocksInCache: 10,
    animateRows: true,
    rowSelection: 'single',
    enableCellTextSelection: true,
    ensureDomOrder: true,
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    getRowStyle: (params) => {
      const cancelled = isCancelled(params.data);

      return {
        backgroundColor: params.node.rowIndex % 2 === 0
          ? (isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
          : 'transparent',
        textDecoration: cancelled ? 'line-through' : 'none',
        opacity: cancelled ? 0.6 : 1,
        color: cancelled ? (isDark ? '#9CA3AF' : '#6B7280') : theme.palette.text.primary,
      };
    },
    onGridReady: (params) => {
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', () => {
        setTimeout(() => params.api.sizeColumnsToFit());
      });
    },
  }), [isDark, theme.palette.text.primary]);

  // Handle Export PDF
  const handleExportPDF = useCallback(async () => {
    if (!gridRef.current?.api) {
      showAlert('Grid not ready', 'error');
      return;
    }

    if (filteredTransactions.length === 0) {
      showAlert('No transactions to export', 'warning');
      return;
    }

    setIsExporting(true);

    try {
      showAlert('Generating PDF...', 'info');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const gstTransactions = filteredTransactions.filter(t =>
        t.transactionDetails?.gstApplicable
      );

      if (gstTransactions.length === 0) {
        showAlert('No GST transactions found for export', 'warning');
        return;
      }

      for (let i = 0; i < gstTransactions.length; i++) {
        if (i > 0) {
          pdf.addPage();
        }

        const transaction = gstTransactions[i];
        const billHtml = generateTransactionBillHTML(transaction, customers);

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = billHtml;
        tempDiv.style.width = '210mm';
        tempDiv.style.padding = '0';
        tempDiv.style.margin = '0';
        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = imgWidth / imgHeight;

        const margin = 5;
        const maxWidth = pageWidth - 2 * margin;
        const maxHeight = pageHeight - 2 * margin;

        let finalWidth, finalHeight;

        if (imgWidth > imgHeight) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / ratio;
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = maxHeight * ratio;
          }
        } else {
          finalHeight = maxHeight;
          finalWidth = maxHeight * ratio;
          if (finalWidth > maxWidth) {
            finalWidth = maxWidth;
            finalHeight = maxWidth / ratio;
          }
        }

        const x = (pageWidth - finalWidth) / 2;
        const y = (pageHeight - finalHeight) / 2;

        pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);
        document.body.removeChild(tempDiv);
      }

      const fileName = `Transaction_History_${format(dates.startDate, 'dd-MM-yyyy')}_to_${format(dates.endDate, 'dd-MM-yyyy')}.pdf`;
      pdf.save(fileName);

      showAlert('PDF exported successfully', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showAlert('Failed to generate PDF', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [dates, customers, showAlert, filteredTransactions]);

  // Handle Export Excel
  const handleExportExcel = useCallback(() => {
    if (!gridRef.current?.api) {
      showAlert('Grid not ready', 'error');
      return;
    }

    const gstTransactions = filteredTransactions.filter(transaction => {
      const isGST = transaction.transactionDetails?.gstApplicable === true;
      const isCancelled =
        transaction?.transactionDetails?.type === "cancelled" ||
        transaction?.status === "cancelled" ||
        transaction?.isCancelled === true ||
        transaction?.transactionDetails?.isCancelled === true ||
        transaction?.cancelledAt !== undefined;

      return isGST && !isCancelled;
    });

    if (gstTransactions.length === 0) {
      showAlert('No GST transactions found to export', 'warning');
      return;
    }

    try {
      const excelData = gstTransactions.map((transaction, index) => {
        const customer = customers.find(c => c.id === transaction.customer);
        return {
          'Sr. No.': index + 1,
          'Date': transaction.date,
          'Customer Name': customer?.name || 'N/A',
          'Bill No': transaction.transactionDetails?.billNo || 'N/A',
          'Items Count': transaction.items?.reduce((total, item) => total + (item.items?.length || 1), 0) || 0,
          'Subtotal': transaction.calculatedTotals?.subtotal || 0,
          'CGST': transaction.summary?.cgst || 0,
          'SGST': transaction.summary?.sgst || 0,
          'Grand Total': transaction.summary?.grandTotal || 0,
          'Paid': transaction.summary?.paymentDone || 0,
          'Pending': transaction.summary?.pendingAmount || 0,
          'Status': (transaction.summary?.pendingAmount || 0) === 0 ? 'Paid' : 'Pending',
          'GST': 'Yes',
        };
      });

      const totals = {
        'Sr. No.': 'TOTAL',
        'Date': '',
        'Customer Name': '',
        'Bill No': '',
        'Items Count': excelData.reduce((sum, row) => sum + row['Items Count'], 0),
        'Subtotal': excelData.reduce((sum, row) => sum + row['Subtotal'], 0),
        'CGST': excelData.reduce((sum, row) => sum + row['CGST'], 0),
        'SGST': excelData.reduce((sum, row) => sum + row['SGST'], 0),
        'Grand Total': excelData.reduce((sum, row) => sum + row['Grand Total'], 0),
        'Paid': excelData.reduce((sum, row) => sum + row['Paid'], 0),
        'Pending': excelData.reduce((sum, row) => sum + row['Pending'], 0),
        'Status': '',
        'GST': '',
      };

      excelData.push({});
      excelData.push(totals);

      const headers = Object.keys(excelData[0]);
      const dataWithHeaders = [
        headers,
        ...excelData.map(row => headers.map(key => row[key]))
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);

      const colWidths = [
        { wch: 8 }, { wch: 12 }, { wch: 25 }, { wch: 10 },
        { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 },
        { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 8 }
      ];
      worksheet['!cols'] = colWidths;

      const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
      headerRange.s.r = 0;
      headerRange.e.r = 0;

      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "E0E0E0" } },
          alignment: { horizontal: "center" }
        };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'GST Transactions');

      const fileName = `GST_Transactions_${format(dates.startDate, 'dd-MM-yyyy')}_to_${format(dates.endDate, 'dd-MM-yyyy')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showAlert(`Exported ${gstTransactions.length} GST transactions successfully`, 'success');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert('Failed to export Excel file', 'error');
    }
  }, [customers, dates, showAlert, filteredTransactions]);

  // Handle Clear Filters
  const handleClearFilters = useCallback(() => {
    setCustomerFilter('all');
    setSearchInput('');
    showAlert('Filters cleared', 'info');
  }, [showAlert]);

  // Custom Loading Overlay
  const loadingOverlayComponent = useMemo(() => {
    return function CustomLoadingOverlay() {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress
              size={50}
              sx={{
                color: '#2563EB',
                mb: 2,
              }}
            />
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: isDark ? '#e0e0e0' : '#1a1a1a',
              }}
            >
              Loading transactions...
            </Typography>
          </Box>
        </Box>
      );
    };
  }, [isDark]);

  // Custom No Rows Overlay
  const noRowsOverlayComponent = useMemo(() => {
    return function CustomNoRowsOverlay() {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.4), rgba(30, 41, 59, 0.6))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.9))',
            backdropFilter: 'blur(10px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }}>
            <ReceiptIcon sx={{
              fontSize: 40,
              color: isDark ? '#94A3B8' : '#9CA3AF',
              opacity: 0.6,
            }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: isDark ? '#A0A0A0' : '#666',
              fontWeight: 500,
            }}
          >
            {searchInput ? 'No transactions found' : 'No transactions available'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#71717A' : '#9CA3AF',
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            {searchInput
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'Transactions will appear here once you create them.'}
          </Typography>
        </Box>
      );
    };
  }, [isDark, searchInput]);

  // Loading states
  const isLoading = contextLoading || isPageLoading;
  const isProcessing = isSearching || isFiltering || isExporting;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: '#2563EB',
              mb: 2,
            }}
          />
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              background: isDark
                ? 'linear-gradient(135deg, #60A5FA, #93C5FD)'
                : 'linear-gradient(135deg, #2563EB, #3B82F6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Loading transactions...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: '100vh',
          py: 4,
        }}
      >
        {/* Backdrop for loading states */}
        <Backdrop
          open={isExporting}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: '#fff',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress color="inherit" size={50} />
            <Typography sx={{ mt: 2, fontWeight: 600 }}>Generating PDF...</Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              This may take a moment
            </Typography>
          </Box>
        </Backdrop>

        <Container maxWidth="xl">
          {/* Alert */}
          {alertMessage && (
            <Fade in={!!alertMessage}>
              <Alert
                severity={alertType}
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  backgroundColor: isDark
                    ? 'rgba(30, 41, 59, 0.7)'
                    : 'rgba(255, 255, 255, 0.8)',
                }}
                onClose={() => setAlertMessage('')}
              >
                {alertMessage}
              </Alert>
            </Fade>
          )}

          {/* Header */}
          <Box sx={{
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 3,
          }}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  mb: 0.5,
                  background: isDark
                    ? 'linear-gradient(135deg, #F1F5F9, #E2E8F0)'
                    : 'linear-gradient(135deg, #1E293B, #374151)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Transaction History
              </Typography>
              <Typography
                sx={{
                  color: isDark ? '#A0A0A0' : '#6B7280',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <HistoryIcon fontSize="small" />
                View and manage customer transactions
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', md: 'flex-end' },
            }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                variant="outlined"
                disabled={isFiltering}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                  color: theme.palette.mode === 'dark' ? '#60A5FA' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(37, 99, 235, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                }}
              >
                Refresh
              </Button>

              <Button
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                variant="outlined"
                disabled={customerFilter === 'all' && !searchInput}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                  color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(107, 114, 128, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                }}
              >
                Clear Filters
              </Button>

              <Button
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                variant="outlined"
                disabled={filteredTransactions.length === 0}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#34D399' : '#10B981',
                  color: theme.palette.mode === 'dark' ? '#34D399' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(16, 185, 129, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                }}
              >
                Export Excel
              </Button>

              <Button
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                variant="contained"
                disabled={isExporting || filteredTransactions.length === 0}
                sx={{
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #DC2626, #B91C1C)'
                    : 'linear-gradient(135deg, #DC2626, #B91C1C)',
                  borderRadius: '10px',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(220, 38, 38, 0.5)'
                    : '0 4px 12px rgba(220, 38, 38, 0.4)',
                  fontWeight: 600,
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(220, 38, 38, 0.5)'
                    : '1px solid rgba(220, 38, 38, 0.4)',
                }}
              >
                Export PDF
              </Button>
            </Box>
          </Box>

          {/* Stats Summary */}
          {/* Stats Summary - Design Fixed */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              {/* Card 1: Total Transactions */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(30, 41, 59, 0.6))'
                      : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                    border: `1px solid ${alpha(
                      isDark ? '#475569' : '#e2e8f0',
                      isDark ? 0.3 : 0.5
                    )}`,
                    height: '100%',
                    width: '180px',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 40px rgba(0,0,0,0.4)'
                        : '0 12px 40px rgba(0,0,0,0.08)',
                      borderColor: isDark ? '#60A5FA' : '#2563EB',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                    }}>
                      <ReceiptIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? '#94A3B8' : '#64748B',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Total
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#94A3B8' : '#64748B',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Total Transactions
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: isDark ? '#F1F5F9' : '#0F172A',
                      fontSize: '2rem',
                      mb: 1,
                    }}
                  >
                    {filteredTransactions.length}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                    <Box sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: filteredTransactions.length > 0 ? '#10B981' : '#94A3B8',
                    }} />
                    <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.7rem' }}>
                      {filteredTransactions.length > 0 ? 'Active' : 'No transactions'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Card 2: Total Revenue */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(30, 41, 59, 0.6))'
                      : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                    border: `1px solid ${alpha(
                      isDark ? '#475569' : '#e2e8f0',
                      isDark ? 0.3 : 0.5
                    )}`,
                    height: '100%',
                    width: '180px',
                    minHeight: '130px',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 40px rgba(0,0,0,0.4)'
                        : '0 12px 40px rgba(0,0,0,0.08)',
                      borderColor: isDark ? '#34D399' : '#10B981',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    }}>
                      <TrendingUpIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? '#94A3B8' : '#64748B',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Revenue
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#94A3B8' : '#64748B',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#10B981',
                      fontSize: '1.7rem',
                      mb: 1,
                    }}
                  >
                    ₹{filteredTransactions.reduce((sum, t) => {
                      const amount = t.calculatedTotals?.subtotal ||
                        t.summary?.grandTotal ||
                        t.transactionDetails?.originalBillAmount ||
                        t.totalAmount ||
                        0;
                      return sum + parseFloat(amount);
                    }, 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                    <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.7rem' }}>
                      Avg: ₹{(filteredTransactions.length > 0
                        ? Math.round(filteredTransactions.reduce((sum, t) => {
                          const amount = t.calculatedTotals?.subtotal ||
                            t.summary?.grandTotal ||
                            t.transactionDetails?.originalBillAmount ||
                            t.totalAmount ||
                            0;
                          return sum + parseFloat(amount);
                        }, 0) / filteredTransactions.length)
                        : 0).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Card 3: Total Pending */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(30, 41, 59, 0.6))'
                      : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                    border: `1px solid ${alpha(
                      isDark ? '#475569' : '#e2e8f0',
                      isDark ? 0.3 : 0.5
                    )}`,
                    height: '100%',
                    width: '180px',
                    minHeight: '130px',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 40px rgba(0,0,0,0.4)'
                        : '0 12px 40px rgba(0,0,0,0.08)',
                      borderColor: isDark ? '#F87171' : '#EF4444',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                    }}>
                      <PaymentIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? '#94A3B8' : '#64748B',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Pending
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#94A3B8' : '#64748B',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Total Pending Amount
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#EF4444',
                      fontSize: '1.7rem',
                      mb: 1,
                    }}
                  >
                    ₹{filteredTransactions.reduce((sum, t) => {
                      let pending = 0;
                      if (t.transactionDetails?.balanceDue) pending = parseFloat(t.transactionDetails.balanceDue);
                      else if (t.summary?.pendingAmount) pending = parseFloat(t.summary.pendingAmount);
                      else if (t.calculatedTotals?.fineBalance) pending = parseFloat(t.calculatedTotals.fineBalance);
                      return sum + pending;
                    }, 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                    <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.7rem' }}>
                      {filteredTransactions.filter(t => {
                        let pending = 0;
                        if (t.transactionDetails?.balanceDue) pending = parseFloat(t.transactionDetails.balanceDue);
                        else if (t.summary?.pendingAmount) pending = parseFloat(t.summary.pendingAmount);
                        else if (t.calculatedTotals?.fineBalance) pending = parseFloat(t.calculatedTotals.fineBalance);
                        return pending > 0;
                      }).length} bills pending
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Card 4: Fully Paid */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(30, 41, 59, 0.6))'
                      : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                    border: `1px solid ${alpha(
                      isDark ? '#475569' : '#e2e8f0',
                      isDark ? 0.3 : 0.5
                    )}`,
                    height: '100%',
                    width: '180px',
                    minHeight: '130px',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 40px rgba(0,0,0,0.4)'
                        : '0 12px 40px rgba(0,0,0,0.08)',
                      borderColor: isDark ? '#34D399' : '#22C55E',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                    }}>
                      <CheckCircleIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? '#94A3B8' : '#64748B',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Paid
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#94A3B8' : '#64748B',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Fully Paid Bills
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#22C55E',
                      fontSize: '2rem',
                      mb: 1,
                    }}
                  >
                    {filteredTransactions.filter(t => {
                      let pending = 0;
                      if (t.transactionDetails?.balanceDue) pending = parseFloat(t.transactionDetails.balanceDue);
                      else if (t.summary?.pendingAmount) pending = parseFloat(t.summary.pendingAmount);
                      else if (t.calculatedTotals?.fineBalance) pending = parseFloat(t.calculatedTotals.fineBalance);
                      return pending <= 0;
                    }).length}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                    <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.7rem' }}>
                      {filteredTransactions.length > 0
                        ? `${Math.round((filteredTransactions.filter(t => {
                          let pending = 0;
                          if (t.transactionDetails?.balanceDue) pending = parseFloat(t.transactionDetails.balanceDue);
                          else if (t.summary?.pendingAmount) pending = parseFloat(t.summary.pendingAmount);
                          else if (t.calculatedTotals?.fineBalance) pending = parseFloat(t.calculatedTotals.fineBalance);
                          return pending <= 0;
                        }).length / filteredTransactions.length) * 100)}% completion rate`
                        : 'No data'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Active Filters Summary */}
          {(customerFilter !== 'all' || searchInput) && (
            <Fade in>
              <Box sx={{
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                padding: 2,
                borderRadius: '12px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
              }}>
                <FilterIcon sx={{ color: '#6B7280', fontSize: '1rem' }} />
                <Typography variant="body2" color="text.secondary">
                  Active filters:
                </Typography>

                {customerFilter !== 'all' && (
                  <Chip
                    label={`Customer: ${customers.find(c => c.id === customerFilter)?.name || 'Selected Customer'}`}
                    size="small"
                    onDelete={() => {
                      setCustomerFilter('all');
                    }}
                    color="primary"
                    variant="filled"
                  />
                )}

                {searchInput && (
                  <Chip
                    label={`Search: "${searchInput}"`}
                    size="small"
                    onDelete={() => setSearchInput('')}
                    color="secondary"
                    variant="filled"
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Showing {filteredTransactions.length} of {customerTransactions.length} transactions
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Filters Section */}
          <Card sx={{
            mb: 4,
            borderRadius: '16px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
            backdropFilter: 'blur(10px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={dates.startDate}
                    onChange={(newValue) => setDates(prev => ({ ...prev, startDate: newValue }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End Date"
                    value={dates.endDate}
                    onChange={(newValue) => setDates(prev => ({ ...prev, endDate: newValue }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    options={[{ id: 'all', name: 'All Customers' }, ...customers]}
                    value={customerFilter === 'all'
                      ? { id: 'all', name: 'All Customers' }
                      : customers.find(c => c.id === customerFilter) || null}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(event, newValue) => {
                      setCustomerFilter(newValue ? newValue.id : 'all');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        placeholder="Search by name, phone, address..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                            width: '300px',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* AG Grid Table */}
          <Card
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              height: 'calc(100vh - 300px)',
              minHeight: '500px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.2))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))',
              backdropFilter: 'blur(10px)',
              border: isDark
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            {isProcessing && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  height: 3,
                  background: 'linear-gradient(90deg, #2563EB, #3B82F6)',
                }}
              />
            )}

            <CardContent sx={{ p: 0, height: '100%' }}>
              <div
                className={`ag-theme-premium-${theme.palette.mode}`}
                style={{
                  width: '100%',
                  height: '100%',
                }}
              >
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={filteredTransactions}
                  defaultColDef={defaultColDef}
                  gridOptions={gridOptions}
                  loadingOverlayComponent={loadingOverlayComponent}
                  noRowsOverlayComponent={noRowsOverlayComponent}
                  headerHeight={50}
                  rowHeight={64}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                  suppressContextMenu={true}
                  suppressMenuHide={false}
                  suppressMovableColumns={true}
                />
              </div>
            </CardContent>
          </Card>

          {/* Redesigned Transaction Details Modal */}
          <Dialog
            open={openModal}
            onClose={() => setOpenModal(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                backdropFilter: 'blur(20px)',
                backgroundColor: alpha(
                  isDark ? theme.palette.background.paper : '#ffffff',
                  isDark ? 0.3 : 0.6
                ),
                border: `1px solid ${alpha(
                  isDark ? theme.palette.divider : '#e2e8f0',
                  isDark ? 0.2 : 0.3
                )}`,
                maxHeight: '90vh',
              }
            }}
          >
            {selectedTransaction && (() => {
              const allItems = getAllItemsForModal(selectedTransaction);
              const totals = calculateModalTotals(allItems);

              // Calculate GST
              let cgst = 0, sgst = 0, igst = 0, grandTotal = totals.totalAmount;
              const showGST = selectedTransaction.transactionDetails?.gstApplicable === true;
              const gstType = selectedTransaction.transactionDetails?.gstType || 'cgst_sgst';

              if (showGST && totals.totalAmount > 0) {
                if (gstType === 'igst') {
                  igst = totals.totalAmount * 0.03;
                  grandTotal = totals.totalAmount + igst;
                } else {
                  cgst = totals.totalAmount * 0.015;
                  sgst = totals.totalAmount * 0.015;
                  grandTotal = totals.totalAmount + cgst + sgst;
                }
              }

              const formatNumber = (num, decimals = 3) => {
                if (!num || isNaN(num)) return "0";
                return parseFloat(num).toFixed(decimals);
              };

              return (
                <>
                  <DialogTitle sx={{
                    fontWeight: 700,
                    background: isDark
                      ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                      : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.1 : 0.2
                    )}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SummarizeIcon fontSize="small" />
                      Bill Details - {selectedTransaction.transactionDetails?.billNo || selectedTransaction.id?.slice(-6) || 'N/A'}
                    </Box>
                    <IconButton onClick={() => setOpenModal(false)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>

                  <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ overflowX: 'auto' }}>
                      <TableContainer sx={{ maxHeight: 'calc(90vh - 120px)' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{
                              backgroundColor: alpha(isDark ? '#1e293b' : '#f8fafc', 0.8),
                            }}>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>S.No.</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Serial No.</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Item Name</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Gross Wt (g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>CZ (g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Pearl (g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Belt (g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Other (g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Net Wt (g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Stone Cost (₹)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>HUID Cost (₹)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Total %</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Metal Rate (₹/g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Fine Wt (g)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Fine Amount (₹)</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Total Amount (₹)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {allItems.map((item, idx) => (
                              <TableRow key={idx} hover>
                                {console.log('Rendering item:', item)}
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{idx + 1}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{item.serialNo || '-'}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{item.name || '-'}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.grossWeight)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.czWeight)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.pearlWeight)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.beltWeight)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.otherWeight)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1, fontWeight: 600, color: '#f59e0b' }}>{formatNumber(item.netWeight)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.stonePrice, 2)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.huidCharge, 2)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.totalPercentage, 1)}%</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.metalRate, 2)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.totalFine, 3)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.amountFromMetal, 2)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 1, fontWeight: 600 }}>₹{formatNumber(item.totalAmount, 2)}</TableCell>
                              </TableRow>
                            ))}

                            {/* Totals Row */}
                            <TableRow sx={{
                              backgroundColor: alpha(isDark ? '#f59e0b' : '#f59e0b', 0.1),
                              borderTop: `2px solid ${alpha(isDark ? '#f59e0b' : '#f59e0b', 0.3)}`,
                            }}>
                              <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                <strong>TOTALS</strong>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalGrossWeight)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalCZWeight)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalPearlWeight)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalBeltWeight)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalOtherWeight)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#f59e0b' }}>{formatNumber(totals.totalNetWeight)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>₹{formatNumber(totals.totalStoneCost, 2)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>₹{formatNumber(totals.totalHuidCharge, 2)}</TableCell>
                              <TableCell colSpan={2}></TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalFine, 3)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>₹{formatNumber(totals.totalAmountFromMetal, 2)}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#f59e0b' }}>₹{formatNumber(totals.totalAmount, 2)}</TableCell>
                            </TableRow>

                            {/* Additional Summary Row */}
                            <TableRow>
                              <TableCell colSpan={16} sx={{ pt: 2, pb: 1 }}>
                                <Box sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: 2,
                                  p: 2,
                                  borderRadius: 2,
                                  background: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5),
                                }}>
                                  <Stack direction="row" spacing={4} flexWrap="wrap">
                                    <Box>
                                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Total Items:
                                      </Typography>
                                      <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                                        {totals.totalItems}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Total Net Weight:
                                      </Typography>
                                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                                        {formatNumber(totals.totalNetWeight)} g
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Total Fine:
                                      </Typography>
                                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                                        {formatNumber(totals.totalFine, 3)} g
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Stone + HUID:
                                      </Typography>
                                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                                        ₹{formatNumber(totals.totalStoneCost + totals.totalHuidCharge, 2)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Fine Amount:
                                      </Typography>
                                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                                        ₹{formatNumber(totals.totalAmountFromMetal, 2)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Total Amount (Before GST):
                                      </Typography>
                                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                                        ₹{formatNumber(totals.totalAmount, 2)}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>

                                {/* GST Summary if applicable */}
                                {showGST && totals.totalAmount > 0 && (
                                  <Box sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    background: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5),
                                    border: `1px solid ${alpha(isDark ? '#8b5cf6' : '#8b5cf6', 0.2)}`,
                                  }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: isDark ? '#a78bfa' : '#7c3aed' }}>
                                      GST Summary
                                    </Typography>

                                    {/* Show GST Status */}
                                    <Box sx={{ mb: 1.5 }}>
                                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                                          GST Applied:
                                        </Typography>
                                        <Chip
                                          label="Yes"
                                          size="small"
                                          color="success"
                                          variant="filled"
                                          sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                      </Stack>
                                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                                          GST Type:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? '#a78bfa' : '#7c3aed' }}>
                                          {gstType === 'igst' ? 'IGST (3%)' : 'CGST + SGST (1.5% each)'}
                                        </Typography>
                                      </Stack>
                                    </Box>

                                    <Divider sx={{ my: 1 }} />

                                    {/* Amount Breakdown with GST */}
                                    <Stack spacing={1}>
                                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                                          Subtotal (Before GST):
                                        </Typography>
                                        <Typography sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                                          ₹{formatNumber(totals.totalAmount, 2)}
                                        </Typography>
                                      </Stack>

                                      {gstType === 'igst' ? (
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                          <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                                            IGST (3%):
                                          </Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#8b5cf6', fontSize: '0.9rem' }}>
                                            ₹{formatNumber(igst, 2)}
                                          </Typography>
                                        </Stack>
                                      ) : (
                                        <>
                                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                                              CGST (1.5%):
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#10b981', fontSize: '0.9rem' }}>
                                              ₹{formatNumber(cgst, 2)}
                                            </Typography>
                                          </Stack>
                                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                                              SGST (1.5%):
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#10b981', fontSize: '0.9rem' }}>
                                              ₹{formatNumber(sgst, 2)}
                                            </Typography>
                                          </Stack>
                                        </>
                                      )}

                                      <Divider sx={{ my: 0.5 }} />

                                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                          Grand Total (with GST):
                                        </Typography>
                                        <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#f59e0b' }}>
                                          ₹{formatNumber(grandTotal, 2)}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                  </Box>
                                )}

                                {/* If GST is not applied, show a message */}
                                {!showGST && (
                                  <Box sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    background: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5),
                                  }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                        Total Amount:
                                      </Typography>
                                      <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#f59e0b' }}>
                                        ₹{formatNumber(totals.totalAmount, 2)}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', textAlign: 'right', mt: 0.5 }}>
                                      (GST Not Applied)
                                    </Typography>
                                  </Box>
                                )}

                                {/* Payment Summary Section */}
                                <Box sx={{
                                  mt: 2,
                                  p: 2,
                                  borderRadius: 2,
                                  background: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5),
                                }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                                    Payment Summary
                                  </Typography>

                                  {/* Regular Payments */}
                                  <Grid container spacing={1} sx={{ mb: 1 }}>
                                    {(selectedTransaction.paymentDetails?.cash > 0) && (
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Cash</Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#10b981' }}>
                                          ₹{selectedTransaction.paymentDetails.cash.toLocaleString('en-IN')}
                                        </Typography>
                                      </Grid>
                                    )}
                                    {(selectedTransaction.paymentDetails?.online > 0) && (
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Online</Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#3b82f6' }}>
                                          ₹{selectedTransaction.paymentDetails.online.toLocaleString('en-IN')}
                                        </Typography>
                                      </Grid>
                                    )}
                                    {(selectedTransaction.paymentDetails?.cheque > 0) && (
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Cheque</Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#8b5cf6' }}>
                                          ₹{selectedTransaction.paymentDetails.cheque.toLocaleString('en-IN')}
                                        </Typography>
                                      </Grid>
                                    )}
                                    {(selectedTransaction.paymentDetails?.other > 0) && (
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary">Other</Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#f59e0b' }}>
                                          ₹{selectedTransaction.paymentDetails.other.toLocaleString('en-IN')}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>

                                  {/* Advance Payment Breakdown */}
                                  {selectedTransaction.paymentDetails?.advancePayment?.enabled &&
                                    selectedTransaction.paymentDetails.advancePayment.breakup &&
                                    Object.values(selectedTransaction.paymentDetails.advancePayment.breakup).some(val => parseFloat(val) > 0) && (
                                      <>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 1 }}>
                                          Advance Payment Breakdown
                                        </Typography>
                                        <Grid container spacing={1}>
                                          {Object.entries(selectedTransaction.paymentDetails.advancePayment.breakup).map(([key, value]) => {
                                            const numValue = parseFloat(value) || 0;
                                            if (numValue <= 0) return null;

                                            let label = key.charAt(0).toUpperCase() + key.slice(1);
                                            let color = '#6b7280';
                                            if (key === 'cash') color = '#10b981';
                                            else if (key === 'online') color = '#3b82f6';
                                            else if (key === 'cheque') color = '#8b5cf6';
                                            else if (key === 'oldGold') { label = 'Old Gold'; color = '#f59e0b'; }
                                            else if (key === 'oldSilver') { label = 'Old Silver'; color = '#94a3b8'; }

                                            return (
                                              <Grid item xs={6} sm={3} key={key}>
                                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                <Typography sx={{ fontWeight: 600, color: color }}>
                                                  ₹{numValue.toLocaleString('en-IN')}
                                                </Typography>
                                              </Grid>
                                            );
                                          })}
                                        </Grid>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: `1px solid ${alpha(isDark ? '#f59e0b' : '#f59e0b', 0.2)}` }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                                            Total Advance
                                          </Typography>
                                          <Typography sx={{ fontWeight: 700, color: '#f59e0b' }}>
                                            ₹{(selectedTransaction.paymentDetails.advancePayment.total || 0).toLocaleString('en-IN')}
                                          </Typography>
                                        </Box>
                                      </>
                                    )}

                                  {/* Fine Summary - KEPT AS IT WAS EARLIER */}
                                  {selectedTransaction.calculatedTotals?.totalFineWeight > 0 && (
                                    <>
                                      <Divider sx={{ my: 1.5 }} />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#8b5cf6' }}>
                                        Fine Summary
                                      </Typography>
                                      <Grid container spacing={1}>
                                        <Grid item xs={4}>
                                          <Typography variant="caption" color="text.secondary">Total Fine</Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#8b5cf6' }}>
                                            {selectedTransaction.calculatedTotals.totalFineWeight?.toFixed(3)} gm
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                          <Typography variant="caption" color="text.secondary">Fine Paid</Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#10b981' }}>
                                            {selectedTransaction.calculatedTotals.finePaymentReceived?.toFixed(3)} gm
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                          <Typography variant="caption" color="text.secondary">Fine Balance</Typography>
                                          <Typography sx={{ fontWeight: 600, color: '#f59e0b' }}>
                                            {selectedTransaction.calculatedTotals.fineBalance?.toFixed(3)} gm
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </>
                                  )}

                                  <Divider sx={{ my: 1.5 }} />

                                  {/* Total Paid */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" fontWeight={700}>
                                      Total Paid
                                    </Typography>
                                    <Typography variant="h6" fontWeight={800} color="#10b981">
                                      ₹{(selectedTransaction.paymentDetails?.totalPaymentDone || 0).toLocaleString('en-IN')}
                                    </Typography>
                                  </Box>

                                  {/* Pending Amount */}
                                  {(selectedTransaction.summary?.pendingAmount > 0 || selectedTransaction.calculatedTotals?.fineBalance > 0) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                      <Typography variant="body2" fontWeight={700} color="#ef4444">
                                        Pending Amount
                                      </Typography>
                                      <Typography variant="h6" fontWeight={800} color="#ef4444">
                                        ₹{(selectedTransaction.summary?.pendingAmount || selectedTransaction.calculatedTotals?.fineBalance || 0).toLocaleString('en-IN')}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Payment Status Badge */}
                                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Chip
                                      label={(selectedTransaction.summary?.pendingAmount || selectedTransaction.calculatedTotals?.fineBalance || 0) <= 0 ? 'Fully Paid' : 'Partial Payment'}
                                      size="small"
                                      color={(selectedTransaction.summary?.pendingAmount || selectedTransaction.calculatedTotals?.fineBalance || 0) <= 0 ? 'success' : 'warning'}
                                      variant="filled"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Box>
                                </Box>

                                {/* Customer Information */}
                                <Box sx={{
                                  mt: 2,
                                  p: 2,
                                  borderRadius: 2,
                                  background: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5),
                                }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    Customer Information
                                  </Typography>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Name</Typography>
                                      <Typography sx={{ fontWeight: 600 }}>
                                        {getCustomerName(selectedTransaction.customer)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Date</Typography>
                                      <Typography sx={{ fontWeight: 600 }}>
                                        {selectedTransaction.date}
                                      </Typography>
                                    </Grid>
                                    {selectedTransaction.metaDetails?.handledBy && (
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Handled By</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>
                                          {selectedTransaction.metaDetails.handledBy}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </DialogContent>

                  {/* <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(isDark ? theme.palette.divider : '#e2e8f0', 0.1)}` }}>
                    <Button
                      onClick={() => setOpenModal(false)}
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        background: isDark
                          ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                          : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                      }}
                    >
                      Close
                    </Button>
                  </DialogActions> */}
                </>
              );
            })()}
          </Dialog>
        </Container>
      </Box>

      {/* Cancel Bill Dialog */}
      <Dialog
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.98))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))',
            backdropFilter: 'blur(20px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#DC2626' }}>
              Cancel Bill #{selectedCancelTransaction?.transactionDetails?.billNo || selectedCancelTransaction?.id?.slice(-6)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
          <IconButton
            onClick={handleCancelModalClose}
            size="small"
            disabled={isCancelling}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          {selectedCancelTransaction && (
            <Grid container spacing={3}>
              {/* Bill Information */}
              <Grid item xs={12}>
                <Card sx={{
                  borderRadius: '12px',
                  background: 'rgba(220, 38, 38, 0.05)',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#DC2626', mb: 1 }}>
                      Bill Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Bill No
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.transactionDetails?.billNo || selectedCancelTransaction.id?.slice(-6)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Original Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          ₹{selectedCancelTransaction.calculatedTotals?.subtotal?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.date}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Paid Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#059669' }}>
                          ₹{(selectedCancelTransaction.payment?.totalPaymentDone || 0).toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Refund Amount Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Refund Amount
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cash Refund (₹)"
                      type="text"
                      value={refundAmounts.cash}
                      onChange={(e) => handleRefundChange('cash', e.target.value)}
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bank Transfer (₹)"
                      type="text"
                      value={refundAmounts.bank}
                      onChange={(e) => handleRefundChange('bank', e.target.value)}
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Items to be Updated */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Items to be Updated ({cancellingItems.length})
                </Typography>
                <Box sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  borderRadius: '12px',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  p: 2,
                }}>
                  {cancellingItems.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: '8px',
                        backgroundColor: isDark
                          ? 'rgba(255, 255, 255, 0.02)'
                          : 'rgba(0, 0, 0, 0.01)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            HUID: {item.huid} • Weight: {item.weight}g
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Serial Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.hasConflict ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ textDecoration: 'line-through', color: '#DC2626' }}>
                                  {item.originalSerialNo}
                                </span>
                                <ArrowForwardIcon sx={{ fontSize: '14px', color: '#6B7280' }} />
                                <span style={{ color: '#059669' }}>
                                  {item.updateData.serialNo}
                                </span>
                              </Box>
                            ) : (
                              item.originalSerialNo
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>

              {/* Warning Message */}
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Important Notes:
                  </Typography>
                  <Typography variant="caption" component="div">
                    • This action will mark {cancellingItems.length} jewelry item(s) as available in stock
                    <br />
                    • Serial numbers will be updated if conflicts exist
                    <br />
                    • Refund amount should match the original payment
                    <br />
                    • A cancellation record will be created
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <Button
            onClick={handleCancelModalClose}
            disabled={isCancelling}
            sx={{
              color: '#6B7280',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancellation}
            disabled={isCancelling || refundAmounts.total <= 0}
            startIcon={isCancelling ? <CircularProgress size={16} /> : <CancelIcon />}
            sx={{
              background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
              borderRadius: '10px',
              fontWeight: 600,
            }}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default React.memo(TransactionHistory);